import { WaGreetings } from "./WaGreetings";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send, Search, X, CheckCircle2, XCircle, BookmarkPlus, CalendarClock, Ban, Paperclip } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { api } from "../lib/api";
import { OrgWhatsappAlerts } from "./OrgWhatsappAlerts";

const VARS = ["{الاسم}", "{رقم_الإقامة}", "{رقم_الموظف}", "{الفرع}"];
const MODES = [["pick", "موظفين محددين"], ["branch", "فرع"], ["platform", "منصة"], ["all", "كل الموظفين"]];

export const Picker = ({ employees, ids, setIds }) => {
  const [q, setQ] = useState("");
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? employees.filter((e) => !ids.includes(e.id) && [e.name, e.iqama_number, e.phone, e.rider_id].some((x) => String(x || "").toLowerCase().includes(s))).slice(0, 8) : [];
  }, [q, employees, ids]);
  const name = (id) => employees.find((e) => e.id === id)?.name || id;
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الإقامة أو الجوال" className="pr-8" data-testid="wa-pick-search" /></div>
      {hits.length > 0 && <div className="rounded-lg border border-slate-200 bg-white">{hits.map((e) => (
        <button key={e.id} type="button" onClick={() => { setIds([...ids, e.id]); setQ(""); }} className="flex w-full justify-between px-3 py-1.5 text-sm hover:bg-emerald-50" data-testid={`wa-pick-${e.id}`}>
          <span>{e.name}</span><span className="text-xs text-slate-400" dir="ltr">{e.phone || "بدون جوال"}</span></button>))}</div>}
      <div className="flex flex-wrap gap-1.5" data-testid="wa-picked">{ids.map((id) => (
        <span key={id} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-800">{name(id)}
          <button type="button" onClick={() => setIds(ids.filter((x) => x !== id))}><X className="h-3 w-3" /></button></span>))}</div>
    </div>
  );
};

export const Templates = ({ body, setBody }) => {
  const [list, setList] = useState([]);
  const load = () => api.waTemplates().then((d) => setList(d.templates)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const name = window.prompt("اسم القالب (مثال: تذكير تحديث البيانات)");
    if (!name) return;
    try { await api.waTemplateAdd(name, body); toast.success("حُفظ القالب"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  const del = async (t) => { if (!window.confirm(`حذف القالب «${t.name}»؟`)) return; await api.waTemplateDel(t.id).catch(() => {}); load(); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="wa-templates">
      <span className="text-xs font-semibold text-slate-500">القوالب:</span>
      {list.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-xs text-emerald-800">
          <button type="button" onClick={() => setBody(t.body)} className="ps-2.5 py-1" data-testid={`wa-tpl-${t.id}`}>{t.name}</button>
          <button type="button" onClick={() => del(t)} className="pe-2 text-emerald-400 hover:text-rose-600" data-testid={`wa-tpl-del-${t.id}`}><X className="h-3 w-3" /></button>
        </span>))}
      {!list.length && <span className="text-xs text-slate-400">لا توجد قوالب بعد</span>}
      <button type="button" disabled={!body.trim()} onClick={save} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40" data-testid="wa-tpl-save"><BookmarkPlus className="h-3 w-3" /> حفظ النص كقالب</button>
    </div>
  );
};

const Compose = ({ employees }) => {
  const [later, setLater] = useState(false);
  const [at, setAt] = useState("");
  const [mode, setMode] = useState("pick");
  const [ids, setIds] = useState([]);
  const [val, setVal] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const active = employees.filter((e) => e.status !== "terminated");
  const opts = useMemo(() => [...new Set(active.map((e) => (mode === "branch" ? e.branch : e.platform)).filter(Boolean))].sort(), [active, mode]);
  const count = mode === "pick" ? ids.length : mode === "all" ? active.length : active.filter((e) => (mode === "branch" ? e.branch : e.platform) === val).length;
  const send = async () => {
    const payload = { employee_ids: mode === "pick" ? ids : [], branch: mode === "branch" ? val : "", platform: mode === "platform" ? val : "", all: mode === "all", body };
    if (later) {
      if (!at) { toast.error("حدّد وقت الإرسال"); return; }
      setBusy(true);
      try { await api.waSchedule({ ...payload, send_at: new Date(at).toISOString() }); toast.success(`جُدولت الرسالة إلى ${count} موظف — ${new Date(at).toLocaleString("ar-SA-u-nu-latn")}`); setBody(""); }
      catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الجدولة"); } finally { setBusy(false); }
      return;
    }
    if (!window.confirm(`إرسال الرسالة إلى ${count} موظف؟`)) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.waSend(payload);
      setRes(r); toast.success(`أُرسلت ${r.sent} من ${r.total}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="wa-compose">
      <div className="flex flex-wrap gap-1.5">{MODES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => { setMode(k); setVal(""); }} data-testid={`wa-mode-${k}`}
          className={`rounded-full border px-3 py-1.5 text-xs font-bold ${mode === k ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>))}</div>
      {mode === "pick" && <Picker employees={active} ids={ids} setIds={setIds} />}
      {(mode === "branch" || mode === "platform") && (
        <select value={val} onChange={(e) => setVal(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="wa-group-select">
          <option value="">— اختر {mode === "branch" ? "الفرع" : "المنصة"} —</option>{opts.map((o) => <option key={o}>{o}</option>)}</select>)}
      <Templates body={body} setBody={setBody} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="نص الرسالة… مثال: عزيزي {الاسم}، نذكّرك بتحديث بياناتك." className="w-full rounded-lg border border-slate-200 p-3 text-sm" data-testid="wa-body" />
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">متغيرات:{VARS.map((v) => (
        <button key={v} type="button" onClick={() => setBody((b) => b + v)} className="rounded bg-slate-100 px-2 py-0.5 font-mono hover:bg-emerald-100" data-testid={`wa-var-${v}`}>{v}</button>))}</div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 p-2.5">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={later} onCheckedChange={setLater} data-testid="wa-schedule-switch" /><CalendarClock className="h-4 w-4 text-indigo-600" /> جدولة الإرسال</label>
        {later && <input type="datetime-local" value={at} min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} onChange={(e) => setAt(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="wa-schedule-at" />}
      </div>
      <Button onClick={send} disabled={busy || !body.trim() || !count} className={`w-full gap-2 ${later ? "bg-indigo-600 hover:bg-indigo-700" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="wa-send-bulk">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : later ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />} {later ? "جدولة" : "إرسال"} إلى {count} موظف</Button>
      {res && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="wa-result">
          <p><b className="text-emerald-700">وصلت {res.sent}</b> · فشلت {res.failed_count} · بدون جوال {res.no_phone_count} · الإجمالي {res.total}</p>
          {res.no_phone_count > 0 && <p className="mt-1 text-xs text-amber-700">بدون جوال: {res.no_phone.join("، ")}</p>}
          {res.failed_count > 0 && <p className="mt-1 text-xs text-rose-700">فشلت: {res.failed.map((f) => `${f.name} (${f.reason})`).join("، ")}</p>}
        </div>)}
    </div>
  );
};

const Log = () => {
  const [rows, setRows] = useState(null);
  const [st, setSt] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { setRows(null); api.waMessages(st ? { status: st } : {}).then((d) => setRows(d.messages)).catch(() => setRows([])); }, [st]);
  const list = (rows || []).filter((r) => !q || [r.employee_name, r.phone, r.body].some((x) => String(x || "").includes(q)));
  return (
    <div className="space-y-2" data-testid="wa-log">
      <div className="flex flex-wrap gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو الجوال أو النص" className="max-w-xs" data-testid="wa-log-search" />
        {[["", "الكل"], ["sent", "وصلت"], ["failed", "فشلت"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSt(k)} className={`rounded-full border px-3 text-xs font-bold ${st === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`} data-testid={`wa-log-filter-${k || "all"}`}>{l}</button>))}
      </div>
      {!rows ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : !list.length ? <p className="py-8 text-center text-sm text-slate-400">لا توجد رسائل</p> : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">{list.map((r) => (
          <div key={r.id} className="p-3 text-sm" data-testid={`wa-log-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              {r.status === "sent" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              <b>{r.employee_name}</b><span className="text-xs text-slate-400" dir="ltr">{r.phone}</span>
              <span className="ms-auto text-[11px] text-slate-400">{r.sent_by} · {new Date(r.created_at).toLocaleString("ar-SA-u-nu-latn")}</span>
            </div>
            <p className="mt-1 whitespace-pre-line text-xs text-slate-600">{r.body}</p>
            {r.reason && <p className="mt-0.5 text-[11px] text-rose-600">السبب: {r.reason}</p>}
          </div>))}</div>)}
    </div>
  );
};

const ST = { pending: ["بانتظار الموعد", "bg-amber-100 text-amber-800"], sending: ["جارٍ الإرسال", "bg-sky-100 text-sky-800"], sent: ["أُرسلت", "bg-emerald-100 text-emerald-700"], failed: ["فشلت", "bg-rose-100 text-rose-700"], cancelled: ["ملغاة", "bg-slate-200 text-slate-500"] };
const Scheduled = () => {
  const [items, setItems] = useState(null);
  const load = () => api.waScheduled().then((d) => setItems(d.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const cancel = async (x) => { if (!window.confirm("إلغاء هذه الرسالة المجدولة؟")) return; try { await api.waScheduleCancel(x.id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإلغاء"); } };
  const who = (p) => (p.all ? "كل الموظفين" : p.branch ? `فرع ${p.branch}` : p.platform ? `منصة ${p.platform}` : `${p.employee_ids.length} موظف`);
  if (!items) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />;
  if (!items.length) return <p className="py-8 text-center text-sm text-slate-400">لا توجد رسائل مجدولة — فعّل «جدولة الإرسال» من تبويب الإرسال</p>;
  return (
    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="wa-scheduled">{items.map((x) => {
      const [l, c] = ST[x.status] || ST.pending;
      return (
        <div key={x.id} className="p-3 text-sm" data-testid={`wa-sch-${x.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c}`}>{l}</span>
            <b>{new Date(x.send_at).toLocaleString("ar-SA-u-nu-latn")}</b><span className="text-xs text-slate-500">← {who(x.payload)}</span>
            {x.payload.attachment_id && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700" data-testid={`wa-sch-att-${x.id}`}><Paperclip className="h-3 w-3" /> مع ملف</span>}
            {x.result && <span className="text-xs text-emerald-700">وصلت {x.result.sent} من {x.result.total}</span>}
            {x.error && <span className="text-xs text-rose-600">{x.error}</span>}
            {x.status === "pending" && <button type="button" onClick={() => cancel(x)} className="ms-auto inline-flex items-center gap-1 text-xs text-rose-600" data-testid={`wa-sch-cancel-${x.id}`}><Ban className="h-3.5 w-3.5" /> إلغاء</button>}
          </div>
          <p className="mt-1 whitespace-pre-line text-xs text-slate-600">{x.payload.body}</p>
        </div>);
    })}</div>
  );
};

const AutoTypes = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.waAutoTypes().then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const toggle = async (k, on) => {
    const types = d.types.map((x) => (x.key === k ? { ...x, on } : x));
    setD({ ...d, types });
    try { await api.waAutoTypesSave(types.filter((x) => !x.on).map((x) => x.key)); } catch { toast.error("تعذّر الحفظ"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="wa-auto-types">
      <p className="font-bold text-slate-800">الرسائل التلقائية للموظفين</p>
      <p className="mb-3 text-xs text-slate-500">{d.employee_alerts ? "اختر أنواع الإشعارات التي تُرسل للموظف على واتساب تلقائياً." : "شغّل «رسائل شخصية للموظفين» في البطاقة أعلاه أولاً، ثم اختر الأنواع."}</p>
      <div className="grid gap-2 sm:grid-cols-2">{d.types.map((x) => (
        <label key={x.key} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">{x.label}
          <Switch checked={x.on} onCheckedChange={(v) => toggle(x.key, v)} data-testid={`wa-type-${x.key}`} /></label>))}</div>
    </div>
  );
};

export default function WaMessagesPanel({ employees = [] }) {
  return (
    <div className="space-y-4" data-testid="wa-messages-panel">
      <h3 className="flex items-center gap-2 font-heading font-bold text-slate-800"><MessageCircle className="h-5 w-5 text-emerald-600" /> رسائل واتساب</h3>
      <Tabs defaultValue="send" dir="rtl">
        <TabsList><TabsTrigger value="send" data-testid="wa-tab-send">إرسال</TabsTrigger><TabsTrigger value="log" data-testid="wa-tab-log">السجل</TabsTrigger><TabsTrigger value="scheduled" data-testid="wa-tab-scheduled">المجدولة</TabsTrigger><TabsTrigger value="settings" data-testid="wa-tab-settings">إعدادات الإرسال</TabsTrigger><TabsTrigger value="greetings" data-testid="wa-tab-greetings">تهنئة المناسبات</TabsTrigger></TabsList>
        <TabsContent value="send"><Compose employees={employees} /></TabsContent>
        <TabsContent value="log"><Log /></TabsContent>
        <TabsContent value="scheduled"><Scheduled /></TabsContent>
        <TabsContent value="settings" className="space-y-4"><OrgWhatsappAlerts defaultOpen /><AutoTypes /></TabsContent>
        <TabsContent value="greetings"><WaGreetings employees={employees} /></TabsContent>
      </Tabs>
    </div>
  );
}
